import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import { useWebSocketContext } from 'context/WebSocketConnectionContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { listTextMessages } from 'services/textMessageService';
import { getLocalStorageParseItem, removeLocalStorageItem } from 'utils/localStorageUtils';
import { reorderList } from 'utils/utils';
import { toastConstant } from 'constants/toastmessage';
import { useToast } from 'context/ToastContext';

export default function useTextMessage(leadDetails, caseDetails) {
  const userContext = useUserDetailsContext();
  const websocket = useWebSocketContext();
  const location = useLocation();
  const { addToast } = useToast();

  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState('');
  const [selectedContact, setSelectedContact] = useState(undefined);
  const [newThreadActive, setNewThreadActive] = useState(false);
  const [activeIndex, setActiveIndex] = useState(1); // State for active tab
  const [nextToken, setnextToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPracticeAreas, setSelectedPracticeAreas] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);

  //To do: update websocket logic
  useEffect(() => {
    //Update Side thread list on websocket
    if (websocket?.textMessageData?.body?.from) {
      setMessageThreads((pre) => {
        const threadIndex = pre?.findIndex(
          (item) => item?.from_ === websocket?.textMessageData?.body?.from || item?.to === websocket?.textMessageData.body?.from
        );
        if (threadIndex > -1) {
          let updatedList = pre.map((item) => {
            if (item?.from_ === websocket?.textMessageData?.body?.from || item?.to === websocket?.textMessageData.body?.from) {
              return {
                ...item,
                last_message: websocket?.textMessageData?.body?.message_body,
                last_author:
                  websocket?.textMessageData?.notification_type === 'inbound_text_message'
                    ? 'client'
                    : websocket?.textMessageData?.body?.client_name,
                last_updated: Date.now(),
                unread_reply_count: 1,
              }; // Create a new object with updated name
            }
            return item; // Return the original item if ID doesn't match
          });
          return reorderList(updatedList, threadIndex, 0);
        } else return pre;
      });
    }

    // delivery status Update Logic
    if (websocket?.textMessageData?.body?.SmsSid) {
      setMessageThreads((pre) => {
        return pre.map((item) => {
          if (item?.message_id === websocket?.textMessageData?.body?.SmsSid) {
            return { ...item, last_message_status: websocket?.textMessageData?.body?.SmsStatus }; // Create a new object with updated name
          }
          return item; // Return the original item if ID doesn't match
        });
      });
    }
  }, [websocket]);

  useEffect(() => {
    if (selectedContact?.contact_id || selectedContact === undefined || selectedContact === '' || selectedPracticeAreas) {
      let data = { practice_area_ids: selectedPracticeAreas };
      fetchTextMessages(selectedContact?.contact_id, false, activeIndex === 0 ? '' : false, data);
    }
  }, [selectedContact, activeIndex, selectedPracticeAreas]);

  useEffect(() => {
    if (messageThreads?.length > 0) {
      let currentContactThreads = messageThreads?.filter((t) => t.contact_id === selectedThread?.contact_id);
      let checkThreadActive = currentContactThreads?.some((c) => c.is_active === true);
      setNewThreadActive(checkThreadActive);
    } else {
      setNewThreadActive(true);
    }
  }, [selectedThread, messageThreads]);

  //To do: check rediretion logics
  const fetchTextMessages = async (contactId = false, isNewTread = false, is_read = activeIndex === 0 ? '' : false, data = {}) => {
    listTextMessages(leadDetails?.case_id || caseDetails?.case_id, contactId, null, 15, is_read, null, 'forward', false, pageCount, data)
      .then((response) => {
        if (response?.data?.text_message_threads?.length) {
          setnextToken(response?.data?.next_token);
          let redirectedData = location?.state?.text_message
            ? location?.state?.text_message
            : getLocalStorageParseItem('stateData')?.text_message;
          let filteredData = response?.data?.text_message_threads?.filter((data) =>
            data?.contact_id === redirectedData?.contact_id && data?.to === redirectedData?.to ? redirectedData?.to : redirectedData?.To
          );
          if (isNewTread) {
            handleSelectThread(response.data.text_message_threads?.[0]);
          } else if (redirectedData && filteredData?.length > 0) {
            handleSelectThread(filteredData[0]);
            removeLocalStorageItem('stateData');
          } else {
            selectedContact && handleSelectThread(response.data.text_message_threads?.[0]);
          }
          setMessageThreads(response.data.text_message_threads);
        } else {
          if (selectedContact) {
            handleSelectThread({
              client_name: { first_name: selectedContact?.first_name, last_name: selectedContact?.last_name },
              contact_id: selectedContact?.contact_id,
              to: selectedContact?.phone,
              phone: selectedContact?.phone,
              from_: userContext?.firmPhoneNumber,
              is_active: true,
              latest_phone_number: selectedContact?.phone,
            });
          }
          setMessageThreads([]);
        }
      })
      .catch((error) => console.log(error));
  };

  const loadThreads = () => {
    const isUsingPracticeArea = selectedPracticeAreas?.length > 0;
    if (isLoading) return;
    if (!isUsingPracticeArea) {
      if (nextToken === null || nextToken === undefined || nextToken === '') {
        return;
      }
    } else if (isUsingPracticeArea && isLastPage === true) {
      return;
    }
    setIsLoading(true);
    const data = { practice_area_ids: selectedPracticeAreas };
    const currentPage = pageCount === 1 ? pageCount + 1 : pageCount;
    listTextMessages(
      leadDetails?.case_id || caseDetails?.case_id,
      selectedContact?.contact_id,
      false,
      15,
      activeIndex === 0 ? '' : false,
      nextToken,
      'forward',
      true,
      currentPage,
      data
    )
      .then((response) => {
        if (!isUsingPracticeArea && response?.data?.text_message_threads?.length) {
          setnextToken(response?.data?.next_token);
          setMessageThreads((prev) => [...prev, ...response.data.text_message_threads]);
        } else if (isUsingPracticeArea) {
          setMessageThreads((prev) => [...prev, ...response.data.text_message_threads]);
          setIsLastPage(response?.data?.is_last_page);
          setPageCount(currentPage + 1);
        } else {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND);
        }
      })
      .catch((error) => {
        console.log(error);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND_FAILED
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  function navigateToLatest(contact_id) {
    listTextMessages(caseDetails?.case_id, contact_id, false, 15, '')
      .then((response) => {
        if (response?.data?.text_message_threads?.length) {
          let latestThread = response?.data?.text_message_threads?.find((c) => c.is_active === true);
          handleSelectThread(latestThread);
        } else {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND);
        }
      })
      .catch((error) => {
        console.log(error);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          toastConstant.message.TEXT_MESSAGE_THREAD_NOT_FOUND_FAILED
        );
      });
  }

  const handleSelectThread = (thread) => setSelectedThread(thread);

  return {
    messageThreads,
    setMessageThreads,
    selectedThread,
    handleSelectThread,
    selectedContact,
    setSelectedContact,
    newThreadActive,
    setNewThreadActive,
    activeIndex,
    setActiveIndex,
    fetchTextMessages,
    loadThreads,
    navigateToLatest,
    isLoading,
    selectedPracticeAreas,
    setSelectedPracticeAreas,
    pageCount,
    setPageCount,
    isLastPage,
    setIsLastPage,
  };
}
